// Tide-graph math, ported from the mobile app (TidesNearMe82 src/utils/tideGraph.js).
// The curve is reconstructed from the cubic-Hermite `segments` (NOT the server's
// baked `d`, which clamps interior peaks against un-expanded bounds). Curve and
// every overlay share one expanded y-bounds object so dots land on the line.

// Evaluate tide height (cm) at a Unix-second timestamp: find the segment where
// t0 <= t < t1, then h(u) = a*u^3 + b*u^2 + c*u + d with u = (t - t0)/(t1 - t0).
export function evaluateTideHeight(segments, segmentsScale, timestamp) {
  if (!segments || segments.length === 0) throw new Error('No segments provided')

  const segment = segments.find((seg) => timestamp >= seg.t0 && timestamp < seg.t1)

  if (!segment) {
    // Edge case: exactly at day_end -> use the last segment at u = 1.
    const last = segments[segments.length - 1]
    if (timestamp === last.t1) {
      const a = last.a_i / segmentsScale
      const b = last.b_i / segmentsScale
      const c = last.c_i / segmentsScale
      const d = last.d_i / segmentsScale
      return a + b + c + d
    }
    throw new Error(`Timestamp ${timestamp} outside day range`)
  }

  const dt = segment.t1 - segment.t0
  if (dt <= 0) throw new Error('Invalid segment time range')
  const u = (timestamp - segment.t0) / dt

  const a = segment.a_i / segmentsScale
  const b = segment.b_i / segmentsScale
  const c = segment.c_i / segmentsScale
  const d = segment.d_i / segmentsScale
  return a * u * u * u + b * u * u + c * u + d
}

// Normalized x in [0,1] across the day.
export function getNormalizedX(timestamp, dayStart, dayEnd) {
  const daySeconds = dayEnd - dayStart
  if (daySeconds <= 0) return 0
  return Math.max(0, Math.min(1, (timestamp - dayStart) / daySeconds))
}

// Normalized y in [0,1], inverted (0 = top = yMax, 1 = bottom = yMin).
export function getNormalizedY(heightCm, yMin, yMax) {
  const yRange = yMax - yMin
  if (yRange <= 0) return 0.5
  return 1.0 - (heightCm - yMin) / yRange
}

// True y-range: seed with the server fallback bounds (never shrink below them),
// then widen for each cubic segment's interior extrema (h'(u)=3a*u^2+2b*u+c=0),
// so the curve never clips the frame.
export function computeChartYBounds(segments, segmentsScale, fallbackMin, fallbackMax) {
  if (!segments || segments.length === 0) {
    return { yMin: fallbackMin, yMax: fallbackMax }
  }

  let minH = fallbackMin
  let maxH = fallbackMax
  const considerH = (h) => {
    if (h < minH) minH = h
    if (h > maxH) maxH = h
  }

  for (const seg of segments) {
    const a = seg.a_i / segmentsScale
    const b = seg.b_i / segmentsScale
    const c = seg.c_i / segmentsScale
    const d = seg.d_i / segmentsScale

    considerH(d) // h(0)
    considerH(a + b + c + d) // h(1)

    if (Math.abs(a) < 1e-12) {
      if (Math.abs(b) < 1e-12) continue // linear
      const u = -c / (2 * b)
      if (u > 0 && u < 1) considerH(a * u * u * u + b * u * u + c * u + d)
      continue
    }
    const disc = 4 * b * b - 12 * a * c
    if (disc < 0) continue
    const sqrtDisc = Math.sqrt(disc)
    for (const u of [(-2 * b + sqrtDisc) / (6 * a), (-2 * b - sqrtDisc) / (6 * a)]) {
      if (u > 0 && u < 1) considerH(a * u * u * u + b * u * u + c * u + d)
    }
  }

  return { yMin: minH, yMax: maxH }
}

// Build the SVG path `d` in pixel space by converting each Hermite segment to a
// cubic Bézier. geom = { x0, y0, plotW, plotH, dayStart, dayEnd }.
export function buildCurvePath(segments, segmentsScale, geom, yMin, yMax) {
  const { x0, y0, plotW, plotH, dayStart, dayEnd } = geom
  const daySeconds = dayEnd - dayStart
  const yRange = yMax - yMin
  if (!segments || !segments.length || daySeconds <= 0 || yRange <= 0) return ''

  const px = (nx) => (x0 + nx * plotW).toFixed(2)
  const py = (ny) => (y0 + ny * plotH).toFixed(2)
  let d = ''

  segments.forEach((seg, i) => {
    const a = seg.a_i / segmentsScale
    const b = seg.b_i / segmentsScale
    const c = seg.c_i / segmentsScale
    const dd = seg.d_i / segmentsScale

    const xt0 = (seg.t0 - dayStart) / daySeconds
    const xt1 = (seg.t1 - dayStart) / daySeconds
    const dx = xt1 - xt0

    const h0 = dd
    const h1 = a + b + c + dd
    const ny0 = 1 - (h0 - yMin) / yRange
    const ny1 = 1 - (h1 - yMin) / yRange
    const dy0 = -c / yRange // -(h'(0))/yRange
    const dy1 = -(3 * a + 2 * b + c) / yRange // -(h'(1))/yRange

    if (i === 0) d += `M ${px(xt0)} ${py(ny0)} `
    d += `C ${px(xt0 + dx / 3)} ${py(ny0 + dy0 / 3)} ${px(xt1 - dx / 3)} ${py(ny1 - dy1 / 3)} ${px(xt1)} ${py(ny1)} `
  })

  return d.trim()
}
